import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Loader2,
  CheckCircle2,
  Settings,
  Bell,
  Clock,
  Crown,
  ShoppingBag,
  Package,
  Tag,
  BarChart3,
  Truck,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/context/AuthContext';

export interface BeymenSettings {
  syncFrequency: number;
  notifications: boolean;
  integrations: Record<string, boolean>;
}

interface BeymenSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  storeName: string;
  connectionId: string;
  onSave: (settings: BeymenSettings) => void;
}

export function BeymenSettingsModal({
  isOpen,
  onClose,
  storeName,
  connectionId,
  onSave,
}: BeymenSettingsModalProps) {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  const { user } = useAuth();

  const [syncFrequency, setSyncFrequency] = useState(30);
  const [notifications, setNotifications] = useState(true);
  const [integrations, setIntegrations] = useState([
    { id: 'listings', name: isEnglish ? 'Product Listings' : 'Urun Listesi', icon: ShoppingBag, enabled: true },
    { id: 'orders', name: isEnglish ? 'Orders' : 'Siparisler', icon: Package, enabled: true },
    { id: 'categories', name: isEnglish ? 'Categories' : 'Kategoriler', icon: Tag, enabled: true },
    { id: 'brands', name: isEnglish ? 'Brands' : 'Markalar', icon: Crown, enabled: true },
    { id: 'shipping', name: isEnglish ? 'Shipping' : 'Kargo', icon: Truck, enabled: false },
    { id: 'analytics', name: isEnglish ? 'Analytics' : 'Analitik', icon: BarChart3, enabled: true },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Beymen brand color - Black
  const brandColor = '#000000';

  // Load existing settings
  useEffect(() => {
    const loadSettings = async () => {
      if (!isOpen || !user?.uid || !connectionId) return;

      setIsLoading(true);
      try {
        const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';
        const response = await fetch(
          `${FUNCTIONS_URL}/getBeymenSettings?userId=${user.uid}&connectionId=${connectionId}`
        );
        const result = await response.json();

        if (result.settings) {
          if (result.settings.syncFrequency) {
            setSyncFrequency(result.settings.syncFrequency);
          }
          if (typeof result.settings.notifications === 'boolean') {
            setNotifications(result.settings.notifications);
          }
          if (result.settings.integrations) {
            setIntegrations(prev =>
              prev.map(int => ({
                ...int,
                enabled: result.settings.integrations[int.id] ?? int.enabled,
              }))
            );
          }
        }
      } catch (error) {
        console.error('Error loading Beymen settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [isOpen, user?.uid, connectionId]);

  const handleToggleIntegration = (id: string) => {
    setIntegrations(prev =>
      prev.map(int => (int.id === id ? { ...int, enabled: !int.enabled } : int))
    );
  };

  const handleSave = async () => {
    if (!user?.uid || !connectionId) return;

    setIsSaving(true);
    try {
      const settings: BeymenSettings = {
        syncFrequency,
        notifications,
        integrations: integrations.reduce((acc, int) => {
          acc[int.id] = int.enabled;
          return acc;
        }, {} as Record<string, boolean>),
      };

      const FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-bosphorus-labs.cloudfunctions.net';

      const response = await fetch(`${FUNCTIONS_URL}/saveBeymenSettings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          connectionId,
          settings,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSaveSuccess(true);
        onSave(settings);
        setTimeout(() => {
          setSaveSuccess(false);
          onClose();
        }, 1500);
      }
    } catch (error) {
      console.error('Error saving Beymen settings:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div
          className="px-6 py-5 text-white sticky top-0 z-10"
          style={{ background: `linear-gradient(135deg, ${brandColor} 0%, #1a1a1a 100%)` }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
                <Settings className="w-5 h-5 text-black" />
              </div>
              <div>
                <h2 className="text-lg font-bold">{storeName}</h2>
                <p className="text-white/80 text-sm">
                  {isEnglish ? 'Integration Settings' : 'Entegrasyon Ayarlari'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading ? (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-gray-400" />
              <p className="text-gray-500">
                {isEnglish ? 'Loading settings...' : 'Ayarlar yukleniyor...'}
              </p>
            </div>
          ) : saveSuccess ? (
            <div className="text-center py-8">
              <div
                className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: `${brandColor}15` }}
              >
                <CheckCircle2 className="w-8 h-8" style={{ color: brandColor }} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {isEnglish ? 'Settings Saved!' : 'Ayarlar Kaydedildi!'}
              </h3>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Sync Frequency */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                  <Clock className="w-4 h-4" />
                  {isEnglish ? 'Sync Frequency' : 'Senkronizasyon Sikligi'}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 15, label: '15 dk' },
                    { value: 30, label: '30 dk' },
                    { value: 60, label: '1 saat' },
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => setSyncFrequency(option.value)}
                      className={cn(
                        'py-2.5 px-4 rounded-xl text-sm font-medium transition-all',
                        syncFrequency === option.value
                          ? 'text-white shadow-lg'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      )}
                      style={{
                        backgroundColor: syncFrequency === option.value ? brandColor : undefined,
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notifications */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <Bell className="w-5 h-5 text-gray-600" />
                  <div>
                    <p className="font-medium text-gray-900">
                      {isEnglish ? 'Notifications' : 'Bildirimler'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {isEnglish ? 'Get alerts for new orders' : 'Yeni siparisler icin uyari al'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setNotifications(!notifications)}
                  className={cn(
                    'relative w-12 h-6 rounded-full transition-colors',
                    notifications ? 'bg-black' : 'bg-gray-300'
                  )}
                >
                  <span
                    className={cn(
                      'absolute top-1 w-4 h-4 bg-white rounded-full transition-transform shadow',
                      notifications ? 'right-1' : 'left-1'
                    )}
                  />
                </button>
              </div>

              {/* Integrations */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                  <Settings className="w-4 h-4" />
                  {isEnglish ? 'Data Integrations' : 'Veri Entegrasyonlari'}
                </label>
                <div className="space-y-2">
                  {integrations.map(integration => {
                    const Icon = integration.icon;
                    return (
                      <div
                        key={integration.id}
                        className={cn(
                          'flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer',
                          integration.enabled
                            ? 'border-black/20 bg-black/5'
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                        )}
                        onClick={() => handleToggleIntegration(integration.id)}
                      >
                        <div className="flex items-center gap-3">
                          <Icon
                            className="w-5 h-5"
                            style={{ color: integration.enabled ? brandColor : '#9CA3AF' }}
                          />
                          <span
                            className={cn(
                              'font-medium',
                              integration.enabled ? 'text-gray-900' : 'text-gray-500'
                            )}
                          >
                            {integration.name}
                          </span>
                        </div>
                        <div
                          className={cn(
                            'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all',
                            integration.enabled
                              ? 'border-black bg-black'
                              : 'border-gray-300'
                          )}
                        >
                          {integration.enabled && (
                            <CheckCircle2 className="w-3 h-3 text-white" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Save Button */}
              <button
                onClick={handleSave}
                disabled={isSaving}
                className={cn(
                  'w-full py-3.5 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2',
                  isSaving ? 'bg-gray-300 cursor-not-allowed' : 'hover:opacity-90'
                )}
                style={{ backgroundColor: isSaving ? undefined : brandColor }}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {isEnglish ? 'Saving...' : 'Kaydediliyor...'}
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-5 h-5" />
                    {isEnglish ? 'Save Settings' : 'Ayarlari Kaydet'}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
